import React, { PropsWithChildren } from "react";
import {
  View,
  Platform,
  TextInput,
  StyleSheet,
  TextInputProps,
} from "react-native";
import Icon from "components/Icon";
import Text from "components/Text";
import ImgIcon from "components/imgIcon";

import {
  sn,
  priceFormatter,
  generateFontSize,
  generateFontWithWeight,
} from "utils";
import { sizes, colors } from "assets/constants";

const Input = (props: PropsWithChildren<TextInputT & TextInputProps>) => {
  const {
    fontWeight = "default",
    fontSize = "md",
    lang = "fa",
    icon,
    imgIcon,
    style,
    error,
    value,
    inputRef,
    hideError,
    leftComponent,
    enablePriceFormatter,
    align = "right",
  } = props;

  return (
    <>
      <View style={{ ...styles.container, ...style }}>
        <View style={styles.leftComponentContainer}>{leftComponent}</View>
        <View style={styles.inputContainer}>
          {enablePriceFormatter && (
            <View style={styles.priceViewSection}>
              <Text>{priceFormatter(value)}</Text>
            </View>
          )}
          <TextInput
            ref={inputRef}
            {...(props as TextInputProps)}
            multiline={Platform.OS === "android" && !props.secureTextEntry}
            numberOfLines={1}
            blurOnSubmit={true}
            returnKeyType={"done"}
            value={value}
            placeholderTextColor={colors.placeholder}
            style={{
              ...styles.input,
              textAlign: align,
              borderRadius: sizes.radius.xl,
              fontSize: generateFontSize(fontSize),
              fontFamily: generateFontWithWeight(fontWeight, lang),
              ...(enablePriceFormatter && {
                color: colors.transparent,
                letterSpacing: 1,
              }),
            }}
          />
        </View>

        {(icon || imgIcon) && (
          <View style={styles.iconContainer}>
            {icon && <Icon size={20} {...icon} />}
            {imgIcon && <ImgIcon size={30} {...imgIcon} />}
          </View>
        )}
      </View>
      {!hideError && (
        <View style={styles.errorContainer}>
          <Text size={"xs"} color={colors.danger}>
            {error}
          </Text>
        </View>
      )}
    </>
  );
};

export default Input;

const height = sn(48, 48, 43, 43);
const styles = StyleSheet.create({
  container: {
    width: "100%",
    borderWidth: 2,
    overflow: "hidden",
    flexDirection: "row",
    justifyContent: "flex-end",
    borderColor: colors.border,
    borderRadius: sizes.radius.lg,
    backgroundColor: colors.itemsBg,
  },

  inputContainer: { flex: 1, height, overflow: "hidden" },

  iconContainer: {
    width: sn(45),
    alignItems: "center",
    justifyContent: "center",
  },

  labelContainer: {
    width: "100%",
    alignItems: "flex-end",
  },

  input: {
    zIndex: 1000,
    flex: 1,
    fontSize: 14,
    textAlign: "right",
    paddingHorizontal: 2,
    marginBottom: 0,
    marginTop: 0,
    paddingBottom: 0,
    paddingTop: 0,
    color: colors.textColorDefault,
    textAlignVertical: "center",
    ...(Platform.OS === "web" && { outlineColor: colors.transparent }),
  },

  priceViewSection: {
    width: "100%",
    height: "100%",
    position: "absolute",
    justifyContent: "center",
    paddingHorizontal: 5,
  },

  errorContainer: {
    width: "100%",
    alignSelf: "flex-end",
    height: 20,
    paddingHorizontal: 5,
    bottom: Platform.OS === "web" ? -2 : 3,
  },

  leftComponentContainer: {
    height,
    justifyContent: "center",
  },
});
